/**
 * Auth0-style org/tenant selector for the top bar.
 *
 * The trigger shows the active region flag + org / tenant; the menu mirrors
 * Auth0's account switcher:
 *
 *  - **Switch tenant** — silent token refresh via `SessionManager.switchTenant`;
 *  - **Create tenant** — an org-admin provisioning action (POST `/tenants`),
 *    gated on the `users:create` permission, behind a small dialog;
 *  - **Invite people** — routes to the Team surface (the real invite + roles UI);
 *  - links to the members & settings surfaces.
 *
 * Region flag/labels come from the shared `@authe/fe-commons/region` vocabulary
 * so every app reads the same map.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus, Users, UserPlus, Settings as SettingsIcon } from "lucide-react";
import { useSession } from "@authe/ui-core/session";
import { useCurrentOrg } from "@authe/ui-core/tenant";
import { usePermissions } from "@authe/ui-core/permissions";
import { useApiClient } from "@authe/ui-core/api";
import { unwrapData } from "@authe/fe-commons/api";
import {
  AWS_REGION_ALIASES,
  AWS_REGIONS,
  CONTINENT_FLAG,
  CONTINENT_SHORT_LABEL,
  continentForRegionOrAlias,
} from "@authe/fe-commons/region";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  notifyError,
  notifySuccess,
} from "@authe/ui-core";
import { useTenancySource } from "@/api/adapters";
import type { MyOrganization } from "@/api/tenancySource";
import { appConfig, ROUTES, RESOURCES } from "@/config";

function readTenantIds(claims: Record<string, unknown>): string[] {
  const raw = claims["tenant_ids"] ?? claims["tenants"];
  if (Array.isArray(raw)) return raw.filter((t): t is string => typeof t === "string");
  return [];
}

export function OrgTenantMenu(): React.ReactElement {
  const { session, manager } = useSession();
  const org = useCurrentOrg();
  const { can } = usePermissions();
  const tenancy = useTenancySource();
  const [busy, setBusy] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  // Only the active tenant's friendly name is in the token; resolve the rest
  // (best-effort) from management-api the first time the menu opens.
  const [nameById, setNameById] = React.useState<Record<string, string>>({});
  const [namesLoaded, setNamesLoaded] = React.useState(false);
  // The caller's OWN memberships — a different question from "organizations
  // under my customer". Accepting an invitation to a second org is what puts a
  // row here, and until this list existed that invitation had nowhere to show.
  const [myOrgs, setMyOrgs] = React.useState<MyOrganization[]>([]);

  const tenants = session ? readTenantIds(session.idClaims) : [];
  const current = org.tenantId ?? null;

  const loadNames = React.useCallback(async () => {
    if (namesLoaded || !org.orgId) return;
    setNamesLoaded(true);
    try {
      const rows = await tenancy.listTenants(org.orgId);
      setNameById(Object.fromEntries(rows.map((r) => [r.id, r.name || r.id])));
    } catch {
      // Best-effort: fall back to the active name + raw ids on any failure.
    }
    try {
      setMyOrgs(await tenancy.listMyOrganizations());
    } catch {
      // Best-effort too: a user with one org loses nothing, and the section
      // below only renders when there is somewhere else to go.
    }
  }, [namesLoaded, org.orgId, tenancy]);

  const tenantLabel = (id: string): string =>
    nameById[id] ?? (id === current ? org.tenantName : id);

  const continent = continentForRegionOrAlias(appConfig.region);
  const fullRegion = AWS_REGIONS[AWS_REGION_ALIASES[appConfig.region] ?? appConfig.region];

  // Creating a tenant is an org-admin provisioning action; gate on the same
  // `users` resource that backs the members surface in the JWT.
  const canCreateTenant = can(RESOURCES.team, "create");

  async function switchTo(tenantId: string) {
    if (tenantId === current || busy) return;
    setBusy(true);
    await manager.switchTenant(tenantId, () => window.location.reload());
  }

  /**
   * Move to another organization the caller belongs to.
   *
   * The server does the move: it verifies the membership and rewrites the home
   * tenancy in `app_metadata`. It CANNOT be done from here by asking Auth0 for a
   * different organization — the post-login Action deliberately ignores
   * `/authorize` params when resolving tenancy, so the token would come back
   * pointing at the old org. Re-authenticating afterwards is what picks up the
   * new one.
   */
  async function switchOrgTo(orgId: string) {
    if (orgId === org.orgId || busy) return;
    setBusy(true);
    try {
      await tenancy.setActiveOrganization(orgId);
      await manager.login({ returnTo: window.location.origin });
    } catch {
      // Leave the menu usable rather than stranding the user mid-switch.
      setBusy(false);
    }
  }

  return (
    <>
      <DropdownMenu onOpenChange={(open) => open && void loadNames()}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 px-2" disabled={busy}>
            {continent && (
              <span aria-hidden className="text-sm leading-none">
                {CONTINENT_FLAG[continent]}
              </span>
            )}
            <span className="max-w-[14rem] truncate text-sm font-medium text-foreground">
              {org.orgName}
              <span className="text-muted-foreground"> / {org.tenantName}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Organization</span>
            {continent && (
              <span className="text-xs font-normal text-muted-foreground" title={fullRegion?.name}>
                {CONTINENT_FLAG[continent]} {CONTINENT_SHORT_LABEL[continent]}
              </span>
            )}
          </DropdownMenuLabel>
          <div className="px-2 pb-1 text-sm">
            <div className="font-medium text-foreground">{org.orgName}</div>
            {org.customerName && (
              <div className="text-xs text-muted-foreground">Customer {org.customerName}</div>
            )}
          </div>

          {myOrgs.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Switch organization
              </DropdownMenuLabel>
              {myOrgs.map((o) => (
                <DropdownMenuItem
                  key={o.orgId}
                  disabled={busy || o.isCurrent}
                  onSelect={(e) => {
                    e.preventDefault();
                    void switchOrgTo(o.orgId);
                  }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{o.name}</span>
                  {o.isCurrent && <Check className="h-3.5 w-3.5 shrink-0" />}
                </DropdownMenuItem>
              ))}
            </>
          )}

          {tenants.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Tenants</DropdownMenuLabel>
              {tenants.map((t) => (
                <DropdownMenuItem key={t} onSelect={() => void switchTo(t)} disabled={busy}>
                  {t === current ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                  <span className="truncate">{tenantLabel(t)}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}

          {canCreateTenant && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                Create tenant
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={ROUTES.team}>
              <UserPlus className="h-4 w-4" />
              Invite people
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={ROUTES.team}>
              <Users className="h-4 w-4" />
              Members &amp; team
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={ROUTES.settings}>
              <SettingsIcon className="h-4 w-4" />
              Workspace settings
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateTenantDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={org.orgId}
        onCreated={(tenantId) => {
          if (tenantId) void manager.switchTenant(tenantId, () => window.location.reload());
          else window.location.reload();
        }}
      />
    </>
  );
}

interface CreateTenantResponse {
  id?: string;
  tenant_id?: string;
}

/**
 * Provisioning dialog. POSTs `{ name }` to `/organizations/{orgId}/tenants`
 * through the shared `ApiClient` (so region routing / auth / envelope unwrap are
 * inherited — the canonical path is rewritten onto the management-api
 * create-tenant route) and, on success, switches the session into the new
 * tenant.
 */
function CreateTenantDialog({
  open,
  onOpenChange,
  orgId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onCreated: (tenantId: string | undefined) => void;
}): React.ReactElement {
  const client = useApiClient();
  const [name, setName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const data = unwrapData<CreateTenantResponse>(
        await client.post<CreateTenantResponse>(
          `/organizations/${encodeURIComponent(orgId)}/tenants`,
          { name: trimmed },
        ),
      );
      notifySuccess("Tenant created", { description: `Switching to “${trimmed}”…` });
      onOpenChange(false);
      setName("");
      onCreated(data?.id ?? data?.tenant_id);
    } catch (err) {
      notifyError(err, { title: "Could not create the tenant." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Create tenant</DialogTitle>
            <DialogDescription>
              A tenant is an isolated workspace under your organization. You&apos;ll be switched into it once it&apos;s
              provisioned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="new-tenant-name">Tenant name</Label>
            <Input
              id="new-tenant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Investigations"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? "Creating…" : "Create tenant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
